import { CONFIGURATION_STATUS_TYPE, TEXT_LENGTH } from 'src/shared/constants/constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Form, FormSpy } from 'react-final-form'
import { Alert, Box, Grid2, Stack, Typography } from '@mui/material'
import { TextField } from 'src/shared/form/control/TextField'
import { maxChar, required } from 'src/shared/form/validation/validators'
import { Option } from 'src/shared/form/control'
import { Dispatch, useCallback, useMemo, useState } from 'react'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { getConfigStatusOptions } from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { usePrompt } from 'src/routing/routing-hooks'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM } from 'src/screens/administration/configuration-management/help-instructions-configuration/HelpInstructionsGridUtils'
import { ROUTES } from 'src/routing/routes'
import { AddButton } from 'src/shared/button/Buttons'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'

export interface PrinciplesConfigurationFormValues {
  enName: string
  deName: string
  frName: string
  itName: string
  status: CONFIGURATION_STATUS_TYPE
  enHelpInstructions: string
  deHelpInstructions: string
  frHelpInstructions: string
  itHelpInstructions: string
  helpAndInstructionsKey: string
}

interface PrinciplesConfigurationFormProps {
  mode: 'CREATE' | 'EDIT'
  onSubmit: (values: PrinciplesConfigurationFormValues, setFormDirty: Dispatch<boolean>) => Promise<void>
  initialValues: PrinciplesConfigurationFormValues
}

export const PrinciplesConfigurationForm = ({ onSubmit, initialValues, mode }: PrinciplesConfigurationFormProps) => {
  const { getMessage } = useMessageSource()
  const decorators = createDecorators()
  const navigate = useDelayedNavigate()

  const [formDirty, setFormDirty] = useState<boolean>(false)

  const statusFieldOptions: Option[] = useMemo(() => getConfigStatusOptions(getMessage), [getMessage])

  const handleAddHelpInstructionsLink = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, helpAndInstructionsKey: string) => {
      e.stopPropagation()

      const searchParamsString = `${HELP_AND_INSTRUCTIONS_KEY_SEARCH_PARAM}=${helpAndInstructionsKey}`
      navigate(`${ROUTES.HelpInstructionsConfigurationCreate.params({})}?${searchParamsString}`)
    },
    [navigate],
  )

  usePrompt(getMessage('notification.form.unsaved.changes'), formDirty)

  return (
    <Stack spacing={3}>
      <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
      <Form<PrinciplesConfigurationFormValues>
        initialValues={initialValues}
        onSubmit={(values) => onSubmit(values, setFormDirty)}
        decorators={decorators}
        render={({ handleSubmit }) => {
          return (
            <form id="principles-config-form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={4}>
                <Grid2 container rowSpacing={1} columnSpacing={5}>
                  <Grid2 size={6}>
                    <AutoCompleteField
                      options={statusFieldOptions}
                      name="status"
                      label={getMessage('label.status')}
                      disableClearable={true}
                      required
                      validate={required()}
                    />
                  </Grid2>
                  <Grid2 size={6}></Grid2>

                  <Grid2 size={6}>
                    <TextField name="deName" label={`${getMessage('label.name')} DE`} required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="enName" label={`${getMessage('label.name')} EN`} required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="frName" label={`${getMessage('label.name')} FR`} required validate={required()} />
                  </Grid2>
                  <Grid2 size={6}>
                    <TextField name="itName" label={`${getMessage('label.name')} IT`} required validate={required()} />
                  </Grid2>
                </Grid2>

                <Typography variant="h2">{getMessage('label.help.and.instructions')}</Typography>
                <HtmlEditorField
                  name="deHelpInstructions"
                  label={getMessage('label.language.de')}
                  formLabelProps={{ sx: { fontWeight: 'bold' } }}
                  validate={maxChar(TEXT_LENGTH.XL)}
                />
                <HtmlEditorField
                  name="enHelpInstructions"
                  label={getMessage('label.language.en')}
                  formLabelProps={{ sx: { fontWeight: 'bold' } }}
                  validate={maxChar(TEXT_LENGTH.XL)}
                />
                <HtmlEditorField
                  name="frHelpInstructions"
                  label={getMessage('label.language.fr')}
                  formLabelProps={{ sx: { fontWeight: 'bold' } }}
                  validate={maxChar(TEXT_LENGTH.XL)}
                />
                <HtmlEditorField
                  name="itHelpInstructions"
                  label={getMessage('label.language.it')}
                  formLabelProps={{ sx: { fontWeight: 'bold' } }}
                  validate={maxChar(TEXT_LENGTH.XL)}
                />
              </Stack>

              <FormSpy
                subscription={{ dirty: true }}
                onChange={(props: { dirty: boolean }) => {
                  setFormDirty(props.dirty)
                }}
              />
            </form>
          )
        }}
      />
      {mode === 'EDIT' && (
        <Box display="flex" justifyContent="start">
          <AddButton
            messageKey={'label.configuration.principles.add.help.instructions.link.button'}
            startIcon={<AddOutlinedIcon />}
            onClick={(e) => handleAddHelpInstructionsLink(e, initialValues.helpAndInstructionsKey)}
          />
        </Box>
      )}
    </Stack>
  )
}
